import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Music, Play, Pause, Check, Loader2, X } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const VIDEO_TYPES = ["Video", "Aerial Videos", "Social Reels", "Photo to Video"];

export default function OrderMusicPanel({ order, setOrder }) {
  const [styles, setStyles] = useState([]);
  const [folderLink, setFolderLink] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [usedTracks, setUsedTracks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [playingId, setPlayingId] = useState(null);
  const [savingStyle, setSavingStyle] = useState(false);
  const [pendingRemove, setPendingRemove] = useState(null);

  const videoDeliv = (order.deliverables || []).find((d) => VIDEO_TYPES.includes(d.type) && d.due_source !== "excluded")
    || (order.deliverables || []).find((d) => d.due_source !== "excluded");

  const selectedStyles = order.music_styles || (order.music_style ? [order.music_style] : []);

  const loadUsed = () => api.get(`/orders/${order.id}/music`)
    .then(({ data }) => setUsedTracks(data)).catch(() => {});

  useEffect(() => {
    api.get("/music/styles").then(({ data }) => setStyles(data.styles)).catch(() => {});
    api.get("/dropbox/status").then(({ data }) => setFolderLink(data.music_folder_link)).catch(() => {});
    loadUsed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order.id]);

  const styleKey = selectedStyles.join(",");
  useEffect(() => {
    if (!styleKey || !folderLink) { setSuggestions([]); return; }
    setLoading(true);
    api.get(`/music?styles=${encodeURIComponent(styleKey)}`)
      .then(({ data }) => setSuggestions(data.slice(0, 10)))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [styleKey, folderLink]);

  const toggleStyle = async (s) => {
    const next = selectedStyles.includes(s)
      ? selectedStyles.filter((x) => x !== s)
      : [...selectedStyles, s];
    setSavingStyle(true);
    try {
      const { data } = await api.patch(`/orders/${order.id}`, { music_styles: next });
      setOrder(data);
    } catch (err) { toast.error(apiError(err)); }
    finally { setSavingStyle(false); }
  };

  const applyTrack = async (track) => {
    if (!videoDeliv) { toast.error("No trackable product on this order to attach music to"); return; }
    try {
      await api.post(`/music/${track.id}/usage`, { order_id: order.id, deliverable_id: videoDeliv.id });
      toast.success(`“${track.name}” marked used on ${videoDeliv.type}`);
      setSuggestions((cur) => cur.map((t) => t.id === track.id
        ? { ...t, usages: [...(t.usages || []), { deliverable_id: videoDeliv.id }] } : t));
      loadUsed();
    } catch (err) { toast.error(apiError(err)); }
  };

  const removeUsage = async (trackId, usageId) => {
    try {
      await api.delete(`/music/${trackId}/usage/${usageId}`);
      setSuggestions((cur) => cur.map((t) => t.id === trackId
        ? { ...t, usages: (t.usages || []).filter((u) => u.id !== usageId) } : t));
      loadUsed();
    } catch (err) { toast.error(apiError(err)); }
  };

  if (!folderLink) return null;

  const usedHere = (t) => (t.usages || []).some((u) => u.deliverable_id === videoDeliv?.id);

  return (
    <div className="rounded-xl border border-border bg-card p-5" data-testid="order-music-panel">
      <span className="overline text-muted-foreground flex items-center gap-1.5">
        <Music className="h-3.5 w-3.5 text-fuchsia-400" /> Background Music
      </span>

      {usedTracks.length > 0 && (
        <div className="mt-3 space-y-2" data-testid="order-used-music">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Used on this property</div>
          {usedTracks.map((t) => {
            const isPlaying = playingId === t.id;
            return (
              <div key={t.id} className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-2.5" data-testid={`used-track-${t.id}`}>
                <div className="flex items-center gap-2">
                  <button onClick={() => setPlayingId(isPlaying ? null : t.id)}
                    data-testid={`used-play-${t.id}`}
                    className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition-colors ${isPlaying ? "bg-fuchsia-500 text-white" : "bg-secondary hover:bg-fuchsia-500/20"}`}>
                    {isPlaying ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
                  </button>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-xs font-medium">{t.name}</div>
                    <div className="flex flex-wrap gap-1 text-[10px] text-muted-foreground">
                      {(t.usages || []).map((u) => u.deliverable_type).filter(Boolean).map((dt, i) => (
                        <span key={i} className="rounded bg-secondary px-1 py-0.5">{dt}</span>
                      ))}
                    </div>
                  </div>
                  {(t.usages || []).map((u) => (
                    <button key={u.id} onClick={() => setPendingRemove({ trackId: t.id, trackName: t.name, usage: u })}
                      data-testid={`used-remove-${u.id}`} title="Remove"
                      className="shrink-0 text-muted-foreground hover:text-destructive">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  ))}
                </div>
                {isPlaying && (
                  <audio src={`${BACKEND_URL}/api/music/${t.id}/stream`} controls autoPlay
                    data-testid={`used-audio-${t.id}`} className="mt-2 h-8 w-full"
                    onEnded={() => setPlayingId(null)}
                    onError={() => toast.error("Couldn't play this track")} />
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-3">
        <div className="mb-2 text-xs text-muted-foreground">Tag one or more styles to get track suggestions for this property's video.</div>
        <div className="flex flex-wrap gap-1.5" data-testid="order-music-style-chips">
          {styles.map((s) => {
            const active = selectedStyles.includes(s);
            return (
              <button key={s} onClick={() => toggleStyle(s)} disabled={savingStyle}
                data-testid={`order-style-${s}`}
                className={`rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors disabled:opacity-50 ${active ? "border-fuchsia-500 bg-fuchsia-500/20 text-fuchsia-600 dark:text-fuchsia-300" : "border-border text-muted-foreground hover:bg-secondary"}`}>
                {s}
              </button>
            );
          })}
        </div>
      </div>

      {selectedStyles.length > 0 && (
        <div className="mt-4 space-y-2" data-testid="order-music-suggestions">
          {loading ? (
            <div className="flex justify-center py-4"><Loader2 className="h-4 w-4 animate-spin text-primary" /></div>
          ) : suggestions.length === 0 ? (
            <p className="text-xs text-muted-foreground">No tracks match {selectedStyles.join(", ")} yet. Add styles to tracks on the Music page.</p>
          ) : (
            suggestions.map((t) => {
              const isPlaying = playingId === t.id;
              const used = usedHere(t);
              return (
                <div key={t.id} className="rounded-lg border border-border p-2.5" data-testid={`suggested-track-${t.id}`}>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setPlayingId(isPlaying ? null : t.id)}
                      data-testid={`suggested-play-${t.id}`}
                      className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition-colors ${isPlaying ? "bg-fuchsia-500 text-white" : "bg-secondary hover:bg-fuchsia-500/20"}`}>
                      {isPlaying ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
                    </button>
                    <span className="min-w-0 flex-1 truncate text-xs font-medium">{t.name}</span>
                    <button onClick={() => applyTrack(t)} disabled={used}
                      data-testid={`suggested-use-${t.id}`}
                      className={`shrink-0 rounded-md px-2 py-1 text-[11px] font-medium transition-colors ${used ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400" : "border border-border hover:bg-secondary"}`}>
                      {used ? <span className="flex items-center gap-1"><Check className="h-3 w-3" /> Used</span> : "Use"}
                    </button>
                  </div>
                  {isPlaying && (
                    <audio src={`${BACKEND_URL}/api/music/${t.id}/stream`} controls autoPlay
                      data-testid={`suggested-audio-${t.id}`} className="mt-2 h-8 w-full"
                      onEnded={() => setPlayingId(null)}
                      onError={() => toast.error("Couldn't play this track")} />
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      <AlertDialog open={!!pendingRemove} onOpenChange={(o) => !o && setPendingRemove(null)}>
        <AlertDialogContent data-testid="order-usage-remove-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this track from the property?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes “{pendingRemove?.trackName}” as used on {pendingRemove?.usage?.deliverable_type} for this property. The track itself is not deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="order-usage-remove-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="order-usage-remove-confirm"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                const p = pendingRemove;
                setPendingRemove(null);
                if (p) removeUsage(p.trackId, p.usage.id);
              }}>Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
